import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Wrapper from "../../../components/wrappers/Wrapper";
import { useTranslation } from "react-i18next";
import React, { useContext, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { EDIT_APPOINTMENT } from "../../../enums/PermissionTitles";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import { Settings } from "@mui/icons-material";
import useDelete from "../../../hooks/useDelete";
import { FormProvider, useForm } from "react-hook-form";
import useSubmit from "../../master/species/hooks/useSubmit";
import { SnackbarContext } from "../../../contexts/Snackbar";
import { snackbarSeverity } from "../../../hooks/useSnackbar";
import { PET_BIOMETRICS } from "../../../enums/ApiPaths";
import {
  APPOINTMENT_BIOMETRICS_DATA,
  PET_BIOMETRICS_DATA,
} from "../../../enums/QueryKeys";
import SingleBiometricDetails from "./SingleBiometricDetails";
import useUser from "../../../hooks/useUser";

// outlined: flat bordered accordion used by the single pet view
export default function BiometricsView({ data, queryKey, apiKey, id, outlined = false }) {
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const { isPermissionAllowed } = useContext(GlobalDataContext);
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { t, i18n } = useTranslation();
  const { roles } = useUser();

  const methods = useForm({
    defaultValues: {
      temperature: data?.temperature ?? "",
      heartRate: data?.heartRate ?? "",
      weight: data?.weight ?? "",
      length: data?.length ?? "",
      height: data?.height ?? "",
      height: data?.height ?? "",
    },
  });

  const { deleteFn, ConfirmationDialog } = useDelete(
    data?.id,
    PET_BIOMETRICS,
    queryKey,
    "DeleteBiometricsFieldConfirmation",
    null,
    [queryKey]
  );

  const handleAccordionToggle = () => {
    setIsAccordionExpanded(!isAccordionExpanded);
  };

  async function handleDelete() {
    await deleteFn();
  }

  function isEmpty(obj) {
    return Object.values(obj).every((x) => x === null || x === "");
  }

  const submittion = useSubmit(id, apiKey + id, queryKey, [
    APPOINTMENT_BIOMETRICS_DATA + data?.appointment?.id,
    PET_BIOMETRICS_DATA + data?.pet?.id,
  ]);

  const submitData = async (editedBiometrics) => {
    if (!isEmpty(editedBiometrics)) {
      editedBiometrics = {
        ...editedBiometrics,
        id: data?.id,
      };

      submittion.submitCallback(editedBiometrics);
    } else {
      handleSnackbar("PleaseInputBiometricsData", snackbarSeverity.ERROR);
    }
  };

  return (
    <Box mb={2} data-testid={`pet.biometrics.entry.${data?.id}`}>
      <Accordion
        expanded={isAccordionExpanded}
        onChange={handleAccordionToggle}
        {...(outlined && {
          elevation: 0,
          sx: {
            "&:before": { display: "none" },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "12px !important",
            overflow: "hidden",
          },
        })}
      >
        <AccordionSummary
          expandIcon={
            <IconButton title={t("Settings")}  data-testid={`pet.biometrics.entry.${data?.id}.expand-trigger`}>
              <Settings />
            </IconButton>
          }
        >
          <Wrapper noPaper>
            <Typography width={"100%"} fontWeight={"bold"}>
              {t("Biometrics") + " - " + data?.createdAt}
            </Typography>
            <Stack direction={"row"} spacing={2}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }}
                disabled={!isPermissionAllowed(EDIT_APPOINTMENT) || roles?.user}
                color="error"
                title={t("Delete")}
                data-testid={`pet.biometrics.entry.${data?.id}.delete-trigger`}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          </Wrapper>
        </AccordionSummary>
        <AccordionDetails>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(submitData)}>
              <SingleBiometricDetails data={data} />
            </form>
          </FormProvider>
        </AccordionDetails>
      </Accordion>
      <ConfirmationDialog />
    </Box>
  );
}